import { ECommerceOnboardingFilableResource } from "consts/ecommerce";
import { WebRoutes } from "consts/enpoints/web";
import { UiDateTimeFormatter } from "util/formaters";
import { convertToBooleanSwitch } from "util/transformers";
import { FormAdapter } from "hooks/useForm";
import type { HighRiskMerchant as HighRiskMerchantType } from "features/ecommerce/types";
import { HighRiskMerchant as HighRiskMerchantApi } from "features/ecommerce/api";
import { HighRiskMerchant } from "ui/forms/ECommerce";
import { useOnboardingFileUpload, useOnboardingForm, useOnboardingStepData } from "ui/organizms/ECommerce/Onboarding/common";
import { useDeleteShareholders, useUpdateOrCreateShareholders } from "./shareholder";
import { useOnboardingApplication } from "../../hooks";

export const useDirectorsAndShareholders = () => {
    const { getApplicationModel } = useOnboardingApplication();

    const { data } = useOnboardingStepData<HighRiskMerchantType.OnboardingDirectorsAndShareholders>();

    const { handleDeleteShareholders } = useDeleteShareholders();
    const { handleUpdateOrCreateShareholders } = useUpdateOrCreateShareholders();

    const uploadFiles = useOnboardingFileUpload();

    const withValues = useOnboardingForm({
        save: HighRiskMerchantApi.putOnboardingsHighRiskDirectorsAndShareholdersSteps,
        route: WebRoutes.Onboarding.HighRiskMerchant,
        getApplicationModel
    });

    const formProps: HighRiskMerchant.DirectorsAndShareholders.OnboardingDirectorsAndShareholdersForm = {
        bootstrapData: {},
        initialValues: HighRiskMerchant.DirectorsAndShareholders.getInitialValues(data),
        isBootstrapDataLoading: false,

        onSaveOrCreate: async ({
            ceoPassportFileKey,
            ceoUtilityBillFileKey,
            authSigPassportFileKey,
            authSigUtilityBillFileKey,
            ceoAddressLine1,
            ceoCity,
            ceoCountryOrigin,
            ceoDateOfBirth,
            ceoEmail,
            ceoName,
            ceoPhone,
            ceoPostalCode,
            ceoTitle,
            authSigAddressLine1,
            authSigCity,
            authSigCountryOrigin,
            authSigDateOfBirth,
            authSigEmail,
            authSigName,
            authSigPhone,
            authSigPostalCode,
            authSigTitle,
            isPepAuthSig,
            isPepCeo,
            otherPeople = []
        }, helpers) => {
            try {
                await uploadFiles({
                    resource: ECommerceOnboardingFilableResource.ClientDirectorsShareholdersSteps
                }, [
                    ceoPassportFileKey, HighRiskMerchant.DirectorsAndShareholders.FormField.CeoPassportFileKey
                ], [
                    ceoUtilityBillFileKey, HighRiskMerchant.DirectorsAndShareholders.FormField.CeoUtilityBillFileKey
                ], [
                    authSigPassportFileKey, HighRiskMerchant.DirectorsAndShareholders.FormField.AuthSigPassportFileKey
                ], [
                    authSigUtilityBillFileKey, HighRiskMerchant.DirectorsAndShareholders.FormField.AuthSigUtilityBillFileKey
                ]);

                return handleDeleteShareholders(data.otherPeople)
                    .then(() => handleUpdateOrCreateShareholders(otherPeople))
                    .then(() => withValues({
                        ceoAddressLine1,
                        ceoCity,
                        ceoEmail,
                        ceoName,
                        ceoPhone,
                        ceoPostalCode,
                        ceoTitle,
                        authSigAddressLine1,
                        authSigCity,
                        authSigEmail,
                        authSigName,
                        authSigPhone,
                        authSigPostalCode,
                        authSigTitle,
                        ceoCountryOrigin: ceoCountryOrigin?.[0],
                        authSigCountryOrigin: authSigCountryOrigin?.[0],
                        ceoDateOfBirth: ceoDateOfBirth?.format(UiDateTimeFormatter.Ui),
                        authSigDateOfBirth: authSigDateOfBirth?.format(UiDateTimeFormatter.Ui),
                        isPepAuthSig: convertToBooleanSwitch(isPepAuthSig),
                        isPepCeo: convertToBooleanSwitch(isPepCeo)
                    }, helpers));
            } catch (failedfileResponse) {
                return FormAdapter.fromResponse(failedfileResponse)
            }
        }
    };

    return {
        ...formProps,
        ...HighRiskMerchant.DirectorsAndShareholders.useForm(formProps),
        ...data
    };
};

export default useDirectorsAndShareholders;
